import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Trophy, Users, Brain, ShoppingBag, Building2, Activity } from "lucide-react";
import { Wordmark, Spine } from "../components/Brand.jsx";

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#050E15] text-white ps-aurora overflow-x-hidden">
      <div className="ps-grain absolute inset-0 pointer-events-none" />

      {/* Navbar */}
      <header className="relative max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between z-10">
        <Wordmark />
        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
          <a href="#how" className="hover:text-white">How it works</a>
          <a href="#features" className="hover:text-white">Features</a>
          <a href="#enterprise" className="hover:text-white">Enterprise</a>
          <Link to="/login" className="hover:text-white" data-testid="nav-login">Log in</Link>
        </nav>
        <Link to="/register" className="ps-button-primary text-sm py-2 px-5 inline-flex items-center gap-2" data-testid="nav-register">
          Get Started <ArrowUpRight size={14} />
        </Link>
      </header>

      {/* Hero — editorial asymmetric */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-12 pb-28 grid md:grid-cols-12 gap-12 items-center z-10">
        <div className="md:col-span-7 ps-fade-up">
          <div className="ps-chip mb-7">
            <Sparkles size={12} /> A new kind of wellness
          </div>
          {/* Posture Steps wordmark — refined badge */}
          <div className="mb-8 ps-fade-up">
            <div
              className="inline-flex items-center gap-0.5 px-7 sm:px-9 py-3.5 sm:py-4 rounded-full relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #16a34a 0%, #22c55e 35%, #4ade80 70%, #a7f3d0 100%)",
                boxShadow:
                  "0 18px 60px -18px rgba(74,222,128,0.55), 0 2px 0 0 rgba(255,255,255,0.18) inset, 0 -2px 0 0 rgba(0,0,0,0.18) inset",
              }}
              data-testid="hero-wordmark-badge"
            >
              {/* subtle inner highlight */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  background:
                    "radial-gradient(120% 80% at 20% 0%, rgba(255,255,255,0.35) 0%, transparent 55%)",
                }}
              />
              <span
                className="relative italic font-black tracking-tight leading-none text-[1.75rem] sm:text-[2.25rem] lg:text-[2.6rem]"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  color: "#0b1d3a",
                  letterSpacing: "-0.02em",
                  textShadow: "0 1px 0 rgba(255,255,255,0.25)",
                }}
              >
                POSTURE
              </span>
              <span
                className="relative italic font-black tracking-tight leading-none ml-3 text-[1.75rem] sm:text-[2.25rem] lg:text-[2.6rem]"
                style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  letterSpacing: "-0.02em",
                  background:
                    "linear-gradient(180deg, #f8fafc 0%, #cbd5e1 45%, #64748b 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.15))",
                }}
              >
                STEPS
              </span>
              <span
                className="relative text-[0.55rem] sm:text-[0.65rem] font-bold ml-1 -mt-4 sm:-mt-5"
                style={{ color: "#0b1d3a", opacity: 0.7 }}
              >
                TM
              </span>
            </div>
          </div>
          <h1 className="text-[3.25rem] sm:text-[5rem] lg:text-[6.5rem] font-extrabold leading-[0.95] tracking-[-0.04em] ps-heading">
            Walk Strong <span className="ps-serif ps-italic font-normal">with</span><br />
            <span className="ps-gradient-text">Perfect Posture.</span>
          </h1>
          <p className="mt-8 text-lg text-slate-300 max-w-xl leading-relaxed">
            Posture Steps™ rewards you for both movement <em className="ps-serif text-white">and</em> alignment.
            Every step. Every desk-bound minute. One transformative 365-day journey.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => nav("/register")} className="ps-button-primary inline-flex items-center gap-2" data-testid="hero-get-started">
              Start free <ArrowRight size={18} />
            </button>
            <button onClick={() => nav("/login")} className="ps-button-secondary" data-testid="hero-login">
              I already have an account
            </button>
          </div>
          <div className="mt-12 flex gap-10 text-sm text-slate-400 ps-stagger">
            <div>
              <div className="text-3xl text-white font-bold ps-heading ps-tabular">100K+</div>
              <div className="ps-serif ps-italic">steps tracked</div>
            </div>
            <div>
              <div className="text-3xl text-white font-bold ps-heading ps-tabular">28<span className="text-base text-slate-500">d</span></div>
              <div className="ps-serif ps-italic">avg streak</div>
            </div>
            <div>
              <div className="text-3xl text-white font-bold ps-heading ps-tabular">+14</div>
              <div className="ps-serif ps-italic">score lift</div>
            </div>
          </div>
        </div>

        {/* Phone mockup with editorial flair */}
        <div className="md:col-span-5 relative">
          <div className="relative mx-auto w-[300px] md:w-[340px]">
            <div className="absolute -inset-8 bg-gradient-to-br from-[#4ade80]/30 via-transparent to-[#a78bfa]/20 blur-2xl rounded-[3rem] -z-10" />
            <div className="relative aspect-[9/19] rounded-[3rem] border-[10px] border-[#0E1F2A] bg-[#050E15] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
              <img
                src="https://customer-assets.emergentagent.com/job_posture-steps/artifacts/18ah6wy1_Screenshot%202026-04-26%20at%205.22.48%E2%80%AFPM.png"
                alt="Hiker with backpack overlooking mountain ridge"
                className="absolute inset-0 w-full h-full object-cover opacity-95"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050E15]/35 to-[#050E15]" />
              <div className="absolute bottom-0 inset-x-0 p-6">
                <div className="ps-chip mb-3">
                  <Activity size={11} /> Today
                </div>
                <div className="ps-heading text-5xl font-extrabold ps-tabular">7,842</div>
                <div className="text-xs text-slate-300 mt-1 ps-serif ps-italic">total posture steps</div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="ps-glass rounded-2xl p-3">
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">Active</div>
                    <div className="font-bold text-[#4ade80] ps-tabular text-lg">5,420</div>
                  </div>
                  <div className="ps-glass rounded-2xl p-3">
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">Passive</div>
                    <div className="font-bold text-[#22d3ee] ps-tabular text-lg">2,422</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* floating score badge */}
          <div className="absolute -top-2 -left-6 hidden md:flex flex-col items-center gap-1 ps-glass ps-glow px-5 py-3 ps-float rounded-3xl">
            <Spine size={32} />
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Score</div>
            <div className="text-2xl font-bold ps-heading ps-gradient-text ps-tabular">82</div>
          </div>
          {/* floating streak */}
          <div className="absolute -bottom-4 -right-4 hidden md:flex flex-col items-center ps-glass ps-glow-amber px-5 py-3 ps-float rounded-3xl" style={{ animationDelay: "2s" }}>
            <div className="text-2xl ps-heading font-bold text-[#FBBF24] ps-tabular">27d</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Streak</div>
          </div>
        </div>
      </section>

      {/* Editorial divider */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-4 pb-16 z-10">
        <div className="grid md:grid-cols-12 gap-8 items-end border-b border-[#1B364A] pb-12">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#4ade80] font-bold mb-3">Manifesto</div>
            <p className="ps-serif ps-italic text-[2.2rem] sm:text-[3rem] leading-[1.05] text-white">
              "Every step counts. <br />
              Every posture moment <br />
              <span className="ps-gradient-text not-italic font-bold ps-heading">matters.</span>"
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 text-slate-300 leading-relaxed text-lg">
            Traditional fitness apps measure only the hours you exercise. We reward the other 23 — the desk hour, the commute, the read-by-the-window afternoon — as long as you're holding yourself well. Active and passive. Movement and alignment. <em className="ps-serif text-white">Both</em>.
          </div>
        </div>
      </section>

      {/* Features grid — editorial cards */}
      <section id="features" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 z-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#4ade80] font-bold">Built for the long game</div>
            <h2 className="ps-heading text-4xl sm:text-6xl font-extrabold mt-4 tracking-tight">
              Wellness, <span className="ps-serif ps-italic font-normal">redesigned</span>.
            </h2>
          </div>
          <Link to="/register" className="text-[#4ade80] font-semibold inline-flex items-center gap-1 group">
            Start your 365 <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5 ps-stagger">
          {[
            { icon: Spine, title: "Active + Passive Steps", text: "Earn credit walking AND while sitting tall at your desk, reading, or driving.", color: "#4ade80" },
            { icon: Brain, title: "AI Wellness Coach", text: "Claude-powered coach offers personalized cues, motivation, and progress insights.", color: "#a78bfa" },
            { icon: Trophy, title: "Posture Score™", text: "A single 0–100 score that measures movement, alignment, streaks, and engagement.", color: "#FBBF24" },
            { icon: Users, title: "Posture Circles™", text: "Small accountability groups, friends, leaderboards, and daily encouragement.", color: "#22d3ee" },
            { icon: ShoppingBag, title: "Rewards Marketplace", text: "Redeem Posture Points™ for the Back Transformer™ and partner wellness gear.", color: "#FB7185" },
            { icon: Building2, title: "Enterprise Integration", text: "Personify Health-ready dashboards for HR and corporate wellness teams.", color: "#4ade80" },
          ].map((f) => (
            <div key={f.title} className="ps-card p-7 relative overflow-hidden group">
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-60 transition-opacity"
                style={{ background: f.color }} />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-[#15303E] border border-[#1B364A] flex items-center justify-center mb-5">
                  <f.icon size={24} color={f.color} />
                </div>
                <div className="ps-heading text-2xl font-bold tracking-tight">{f.title}</div>
                <p className="text-slate-400 mt-3 leading-relaxed">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 365-Day Journey CTA */}
      <section id="enterprise" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 z-10">
        <div className="relative rounded-[2.5rem] bg-gradient-to-br from-[#0E1F2A] via-[#15303E] to-[#0E1F2A] border border-[#1B364A] p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center overflow-hidden ps-topo">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#4ade80]/20 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#a78bfa]/15 blur-3xl" />

          <div className="relative">
            <div className="text-xs uppercase tracking-[0.25em] text-[#4ade80] font-bold mb-4">365-day journey</div>
            <h2 className="text-4xl sm:text-6xl font-extrabold ps-heading leading-[1.02] tracking-tight">
              Your <span className="ps-serif ps-italic font-normal">transformation</span><br /> starts <span className="ps-gradient-text">now</span>.
            </h2>
            <p className="text-slate-300 mt-5 text-lg leading-relaxed max-w-md">
              Five stages — Awareness, Consistency, Strength, Lifestyle, Mastery. We'll guide you all the way.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <button onClick={() => nav("/register")} className="ps-button-primary" data-testid="cta-start">Start free</button>
              <button onClick={() => nav("/login")} className="ps-button-secondary" data-testid="cta-login">Log in</button>
            </div>
          </div>

          <div className="relative grid grid-cols-5 gap-3">
            {["Awareness", "Consistency", "Strength", "Lifestyle", "Mastery"].map((s, i) => (
              <div key={s} className="ps-card-elev py-6 text-center relative overflow-hidden">
                <div className="text-3xl font-extrabold ps-heading ps-gradient-text ps-tabular">{i + 1}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-2 font-bold">{s}</div>
                {i === 0 && <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#4ade80] ps-pulse" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="relative border-t border-[#1B364A] py-10 text-center text-slate-500 text-sm z-10">
        <Wordmark className="justify-center" />
        <div className="mt-4 ps-serif ps-italic">"Walk and sit strong with perfect posture."</div>
        <div className="mt-2 text-xs">© 2026 Posture Steps™ — All rights reserved.</div>
      </footer>
    </div>
  );
}
